import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function Suggestion() {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

  const onSubmit = async (data) => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log('Suggestion reçue:', data);
    alert(t('suggestion_success_message'));
    reset();
  };

  return (
    <div className="bg-neutral-50 py-16 min-h-screen">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* EN-TÊTE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-display font-bold text-neutral-900 mb-4">
            {t('suggestions_title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('suggestions_subtitle')}
          </p>
        </motion.div>

        {/* FORMULAIRE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-8 shadow-2xl"
        >
          <h2 className="text-2xl font-display font-bold mb-6 text-neutral-900">
            {t('suggestions_form_title')}
          </h2>
          <p className="text-gray-600 mb-8">
            {t('suggestions_description')}
          </p>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Nom */}
            <div>
              <label className="block font-semibold text-neutral-900 mb-2">
                {t('full_name')}
              </label>
              <input
                {...register("name", { 
                  required: t('contact_name_required'),
                  minLength: { value: 3, message: t('contact_name_min') }
                })}
                className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
                placeholder={t('contact_name_placeholder')}
              />
              {errors.name && <span className="text-red-500 text-sm block mt-1">{errors.name.message}</span>}
            </div>

            {/* Email */}
            <div>
              <label className="block font-semibold text-neutral-900 mb-2">
                {t('email_address')}
              </label>
              <input
                type="email"
                {...register("email", { 
                  required: t('email_required') || 'Email obligatoire',
                  pattern: { 
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('contact_email_invalid')
                  }
                })}
                className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
                placeholder={t('contact_email_placeholder')}
              />
              {errors.email && <span className="text-red-500 text-sm block mt-1">{errors.email.message}</span>}
            </div>

            {/* Téléphone */}
            <div>
              <label className="block font-semibold text-neutral-900 mb-2">
                {t('phone_number')}
              </label>
              <input
                {...register("phone", { 
                  pattern: { 
                    value: /^(\+235|00235)?\s?[67]\d{7}$/, 
                    message: t('contact_phone_invalid') 
                  }
                })}
                className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
                placeholder="+235 66 12 34 56"
              />
              {errors.phone && <span className="text-red-500 text-sm block mt-1">{errors.phone.message}</span>}
            </div>

            {/* Catégorie */}
            <div>
              <label className="block font-semibold text-neutral-900 mb-2">
                {t('suggestion_category')}
              </label>
              <select
                {...register("category", { required: t('category_required') })}
                className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
              >
                <option value="">{t('select_category')}</option>
                <option value="service">{t('category_service')}</option>
                <option value="booking">{t('category_booking')}</option>
                <option value="route">{t('category_route')}</option>
                <option value="price">{t('category_price')}</option>
                <option value="comfort">{t('category_comfort')}</option>
                <option value="other">{t('category_other')}</option>
              </select>
              {errors.category && <span className="text-red-500 text-sm block mt-1">{errors.category.message}</span>}
            </div>

            {/* Message */}
            <div>
              <label className="block font-semibold text-neutral-900 mb-2">
                {t('your_suggestion')}
              </label>
              <textarea
                {...register("message", { 
                  required: t('suggestion_required'),
                  minLength: { value: 10, message: t('suggestion_min_length') }
                })}
                className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition h-40"
                placeholder={t('suggestion_placeholder')}
                rows="7"
              />
              {errors.message && <span className="text-red-500 text-sm block mt-1">{errors.message.message}</span>}
            </div>

            {/* Bouton envoyer */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-primary-600 to-accent-500 text-white font-bold p-5 rounded-xl hover:from-primary-700 hover:to-accent-600 transition transform hover:scale-105 disabled:opacity-50"
            >
              {isSubmitting ? t('sending_suggestion') : t('send_my_suggestion')}
            </button>
          </form>
        </motion.div>

        {/* INFO COMPLÉMENTAIRES */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition text-center">
            <div className="text-4xl mb-4">🚀</div>
            <h3 className="font-bold text-lg mb-2">{t('innovative_ideas')}</h3>
            <p className="text-gray-600 text-sm">{t('innovative_ideas_desc')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition text-center">
            <div className="text-4xl mb-4">💬</div>
            <h3 className="font-bold text-lg mb-2">{t('quick_response')}</h3>
            <p className="text-gray-600 text-sm">{t('quick_response_desc')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition text-center">
            <div className="text-4xl mb-4">🤝</div>
            <h3 className="font-bold text-lg mb-2">{t('collaboration')}</h3>
            <p className="text-gray-600 text-sm">{t('collaboration_desc')}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}